import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, getDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuth } from '../App';
import { Appointment, UserProfile, Medication, Prescription } from '../types';
import { Video, VideoOff, Mic, MicOff, PhoneOff, Clipboard, FileText, Package, Plus, Trash2, Save, CheckCircle2, AlertCircle, Star, User, History, Edit, Activity, X, Share2, RotateCcw } from 'lucide-react';
import Peer, { MediaConnection } from 'peerjs';
import { motion } from 'motion/react';

export default function Consultation() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [patient, setPatient] = useState<UserProfile | null>(null);
  const [doctor, setDoctor] = useState<UserProfile | null>(null);
  
  const formatDuration = (ms: number) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    return `${hours > 0 ? hours + 'h ' : ''}${minutes}m ${seconds}s`;
  };

  const recordCallEnd = async (status: 'ended' | 'failed' | 'missed' = 'ended') => {
    const endTime = Date.now();
    const startTime = activeCallStartTime.current || endTime;
    const durationMs = activeCallStartTime.current ? endTime - activeCallStartTime.current : 0;
    
    const newLog = {
      id: Math.random().toString(36).substr(2, 9),
      startTime,
      endTime,
      status,
      duration: formatDuration(durationMs),
      durationMs
    };

    const updatedLogs = [newLog, ...(callLogs || [])];
    setCallLogs(updatedLogs);
    activeCallStartTime.current = null;

    // Persist to Firestore immediately if appointment exists
    if (id) {
      try {
        await updateDoc(doc(db, 'appointments', id), {
          callLogs: updatedLogs
        });
      } catch (err) {
        console.error('Erro ao salvar logs:', err);
      }
    }
  };

  // Video state
  const [peer, setPeer] = useState<Peer | null>(null);
  const [myStream, setMyStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [currentCall, setCurrentCall] = useState<MediaConnection | null>(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isPeerReady, setIsPeerReady] = useState(false);
  const [callStatus, setCallStatus] = useState<'connecting' | 'ready' | 'connected' | 'error' | 'ended'>('connecting');
  const [activeDuration, setActiveDuration] = useState<string>('00:00');
  const myVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  
  // Prescription state
  const [medications, setMedications] = useState<Medication[]>([]);
  const [selectedMedications, setSelectedMedications] = useState<{ medicationId?: string; name: string; dosage: string; instructions: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPrescribing, setIsPrescribing] = useState(false);
  const [showMedicalHistory, setShowMedicalHistory] = useState(false);
  const [isEditingHistory, setIsEditingHistory] = useState(false);
  const [tempAllergies, setTempAllergies] = useState<string[]>([]);
  const [tempConditions, setTempConditions] = useState<string[]>([]);
  const [previousPrescriptions, setPreviousPrescriptions] = useState<Prescription[]>([]);
  const [digitalCertificate, setDigitalCertificate] = useState<string | null>(null);

  // Call History state
  const [callLogs, setCallLogs] = useState<Appointment['callLogs']>([]);
  const [showCallLogs, setShowCallLogs] = useState(false);
  const [showDoctorInfo, setShowDoctorInfo] = useState(false);
  const [showManualCall, setShowManualCall] = useState(false);
  const [manualCallId, setManualCallId] = useState('');
  const activeCallStartTime = useRef<number | null>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (callStatus === 'connected' && activeCallStartTime.current) {
      interval = setInterval(() => {
        const ms = Date.now() - (activeCallStartTime.current || 0);
        const secs = Math.floor((ms / 1000) % 60);
        const mins = Math.floor((ms / (1000 * 60)) % 60);
        setActiveDuration(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
      }, 1000);
    } else {
      setActiveDuration('00:00');
    }
    return () => clearInterval(interval);
  }, [callStatus]);

  useEffect(() => {
    if (!id || !user) return;

    const unsubscribe = onSnapshot(doc(db, 'appointments', id), async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as Appointment;
        setAppointment({ id: snapshot.id, ...data });
        
        if (data.callLogs) {
          setCallLogs(data.callLogs);
        }
        
        // Fetch patient and doctor info
        const patientDoc = await getDoc(doc(db, 'users', data.patientId));
        if (patientDoc.exists()) setPatient(patientDoc.data() as UserProfile);
        
        if (data.doctorId) {
          const doctorDoc = await getDoc(doc(db, 'users', data.doctorId));
          if (doctorDoc.exists()) {
            const docData = doctorDoc.data() as UserProfile;
            setDoctor(docData);
            setDigitalCertificate(docData.digitalCertificateUrl || null);
          }
        }
      }
    });

    // Fetch medications for prescription
    const fetchMedications = async () => {
      const q = query(collection(db, 'medications'));
      const snapshot = await getDocs(q);
      setMedications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Medication)));
    };
    fetchMedications();

    return () => unsubscribe();
  }, [id, user]);

  useEffect(() => {
    if (!patient?.uid) return;

    const fetchHistory = async () => {
      try {
        const q = query(
          collection(db, 'prescriptions'),
          where('patientId', '==', patient.uid)
        );
        const snapshot = await getDocs(q);
        const history = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Prescription));
        // Sort by date descending
        setPreviousPrescriptions(history.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      } catch (err) {
        console.error('Erro ao buscar histórico:', err);
      }
    };

    fetchHistory();
  }, [patient]);

  // PeerJS setup
  useEffect(() => {
    if (!user || !id) return;

    const newPeer = new Peer(user.uid);
    
    newPeer.on('open', (peerId) => {
      console.log('PeerJS: Conectado com ID:', peerId);
      setIsPeerReady(true);
      setCallStatus('ready');
    });

    newPeer.on('error', (err) => {
      console.error('PeerJS Error:', err);
      setCallStatus('error');
      recordCallEnd('failed');
    });

    newPeer.on('disconnected', () => {
      console.log('PeerJS: Desconectado do servidor');
      setCallStatus('connecting');
      setIsPeerReady(false);
    });

    newPeer.on('close', () => {
      console.log('PeerJS: Conexão destruída');
      setCallStatus('error');
      setIsPeerReady(false);
    });

    setPeer(newPeer);

    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        setMyStream(stream);
        if (myVideoRef.current) myVideoRef.current.srcObject = stream;

        newPeer.on('call', (call) => {
          console.log('PeerJS: Recebendo chamada...');
          setCurrentCall(call);
          call.answer(stream);
          call.on('stream', (incomingStream) => {
            console.log('PeerJS: Recebendo stream remoto (resposta)');
            setRemoteStream(incomingStream);
            setCallStatus('connected');
            activeCallStartTime.current = Date.now();
          });
          call.on('close', () => {
            console.log('PeerJS: Chamada encerrada pelo remoto');
            recordCallEnd();
            setRemoteStream(null);
            setCallStatus('ready');
            setCurrentCall(null);
          });
          call.on('error', (err) => {
            console.error('PeerJS Call Error (incoming):', err);
            recordCallEnd('failed');
            setCallStatus('ready');
            setCurrentCall(null);
          });
        });
      })
      .catch(err => {
        console.error('Erro ao acessar mídia:', err);
      });

    return () => {
      newPeer.destroy();
      myStream?.getTracks().forEach(track => track.stop());
    };
  }, [user, id]);

  // Attach remote stream to video element
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Attach my stream to video element (fallback/redundancy)
  useEffect(() => {
    if (myVideoRef.current && myStream) {
      myVideoRef.current.srcObject = myStream;
    }
  }, [myStream]);

  const callUser = (targetId: string) => {
    if (!peer || !myStream || !isPeerReady) {
      console.warn('PeerJS: Não está pronto para chamar. Peer:', !!peer, 'Stream:', !!myStream, 'Ready:', isPeerReady);
      return;
    }
    
    console.log('PeerJS: Chamando usuário:', targetId);
    const call = peer.call(targetId, myStream);
    setCurrentCall(call);
    
    call.on('stream', (incomingStream) => {
      console.log('PeerJS: Recebendo stream remoto (chamada efetuada)');
      setRemoteStream(incomingStream);
      setCallStatus('connected');
      activeCallStartTime.current = Date.now();
    });

    call.on('close', () => {
      console.log('PeerJS: Chamada encerrada');
      recordCallEnd();
      setRemoteStream(null);
      setCallStatus('ready');
      setCurrentCall(null);
    });

    call.on('error', (err) => {
      console.error('PeerJS Call Error:', err);
      recordCallEnd('failed');
      setCallStatus('ready');
      setCurrentCall(null);
    });
  };

  const toggleVideo = () => {
    if (myStream) {
      const tracks = myStream.getVideoTracks();
      if (tracks.length > 0) {
        const newState = !videoEnabled;
        tracks.forEach(track => {
          track.enabled = newState;
        });
        setVideoEnabled(newState);
      }
    }
  };

  const toggleAudio = () => {
    if (myStream) {
      const tracks = myStream.getAudioTracks();
      if (tracks.length > 0) {
        const newState = !audioEnabled;
        tracks.forEach(track => {
          track.enabled = newState;
        });
        setAudioEnabled(newState);
      }
    }
  };

  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopiedToast(true);
    setTimeout(() => setShowCopiedToast(false), 2000);
  };

  const shareToWhatsApp = () => {
    const url = window.location.href;
    const message = `Olá! Aqui está o link para a nossa teleconsulta na Novità: ${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const updatePatientHistory = async () => {
    if (!patient?.uid) return;
    try {
      await updateDoc(doc(db, 'users', patient.uid), {
        allergies: tempAllergies,
        preExistingConditions: tempConditions
      });
      setPatient(prev => prev ? { ...prev, allergies: tempAllergies, preExistingConditions: tempConditions } : null);
      setIsEditingHistory(false);
    } catch (err) {
      console.error('Erro ao atualizar histórico:', err);
    }
  };

  const endCall = async () => {
    if (currentCall) {
      currentCall.close();
    }
    recordCallEnd();
    setRemoteStream(null);
    setCallStatus('ready');
    setCurrentCall(null);

    if (id) {
      await updateDoc(doc(db, 'appointments', id), { 
        status: 'completed',
        callLogs: callLogs
      });
    }
    if (user?.role === 'doctor') {
      navigate('/dashboard');
    }
  };

  const [evaluation, setEvaluation] = useState({ serviceRating: 10, recommendationRating: 10, feedback: '' });
  const [evaluated, setEvaluated] = useState(false);

  const submitEvaluation = async () => {
    if (!id || !user) return;
    try {
      await addDoc(collection(db, 'evaluations'), {
        appointmentId: id,
        patientId: user.uid,
        ...evaluation,
        createdAt: new Date().toISOString()
      });
      setEvaluated(true);
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
    }
  };

  if (appointment?.status === 'completed' && user?.role === 'patient' && !evaluated) {
    return (
      <div className="min-h-screen bg-bg-light flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-surface p-8 rounded-2xl shadow-2xl max-w-md w-full border border-text-muted/20"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-text-main font-display">Como foi seu atendimento?</h2>
            <p className="text-text-muted mt-2">Sua opinião é muito importante para nós.</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-main mb-2">
                Em uma escala de 0 a 10, qual a nota que você atribuiria para o atendimento da Novità?
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={evaluation.serviceRating}
                onChange={(e) => setEvaluation({ ...evaluation, serviceRating: parseInt(e.target.value) })}
                className="w-full h-2 bg-text-muted/20 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>0</span>
                <span className="font-bold text-primary text-lg">{evaluation.serviceRating}</span>
                <span>10</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-2">
                Em uma escala de 0 a 10, qual a nota que você atribuiria para recomendar a telemedicina da Novità?
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={evaluation.recommendationRating}
                onChange={(e) => setEvaluation({ ...evaluation, recommendationRating: parseInt(e.target.value) })}
                className="w-full h-2 bg-text-muted/20 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>0</span>
                <span className="font-bold text-primary text-lg">{evaluation.recommendationRating}</span>
                <span>10</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-2">Feedback Adicional (Opcional)</label>
              <textarea
                value={evaluation.feedback}
                onChange={(e) => setEvaluation({ ...evaluation, feedback: e.target.value })}
                className="w-full border-text-muted/30 rounded-lg text-sm focus:ring-primary focus:border-primary"
                rows={3}
                placeholder="Conte-nos mais sobre sua experiência..."
              />
            </div>

            <button
              onClick={submitEvaluation}
              className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark shadow-lg transition-all"
            >
              Enviar Avaliação
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const addMedication = (med: Medication | null = null) => {
    if (med) {
      setSelectedMedications([...selectedMedications, { medicationId: med.id, name: med.name, dosage: '', instructions: '' }]);
    } else {
      setSelectedMedications([...selectedMedications, { name: '', dosage: '', instructions: '' }]);
    }
  };

  const removeMedication = (index: number) => {
    setSelectedMedications(selectedMedications.filter((_, i) => i !== index));
  };

  const updateMedication = (index: number, field: string, value: string) => {
    const updated = [...selectedMedications];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedMedications(updated);
  };

  const savePrescription = async () => {
    if (!id || !user || !appointment) return;
    try {
      const presRef = await addDoc(collection(db, 'prescriptions'), {
        appointmentId: id,
        patientId: appointment.patientId,
        doctorId: user.uid,
        medications: selectedMedications,
        digitalSignature: digitalCertificate ? 'ASSINADO DIGITALMENTE' : 'ASSINATURA MANUAL',
        createdAt: new Date().toISOString()
      });
      
      await updateDoc(doc(db, 'appointments', id), {
        prescriptionId: presRef.id,
        status: 'completed'
      });
      
      setIsPrescribing(false);
      endCall();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] bg-text-main flex flex-col lg:flex-row overflow-hidden font-sans">
      {/* Video Area */}
      <div className="flex-grow relative bg-black flex items-center justify-center">
        {/* Dynamic Title Header */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-md px-4">
          <div className="bg-text-main/80 backdrop-blur-md border border-text-muted/20 rounded-2xl p-3 flex flex-col items-center shadow-2xl">
            <h1 className="text-white font-bold text-sm lg:text-base truncate w-full text-center font-display">
              {user?.role === 'doctor' ? (
                `Consulta com: ${patient?.name || 'Carregando...'}`
              ) : (
                `Consulta com: ${doctor?.name ? `Dr(a). ${doctor.name}` : 'Carregando...'}`
              )}
            </h1>
            <div className="flex items-center mt-1 space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                callStatus === 'connected' ? 'bg-primary animate-pulse' :
                callStatus === 'ready' ? 'bg-secondary' :
                callStatus === 'ended' ? 'bg-text-muted' :
                callStatus === 'error' ? 'bg-secondary-dark' : 'bg-text-muted animate-pulse'
              }`} />
              <span className="text-[10px] uppercase tracking-widest font-bold text-text-muted">
                {callStatus === 'connected' ? `Chamada Conectada • ${activeDuration}` :
                 callStatus === 'ready' ? 'Aguardando Conexão' :
                 callStatus === 'ended' ? 'Chamada Encerrada' :
                 callStatus === 'error' ? 'Erro na Conexão' : 'Conectando...'}
              </span>
              {user?.role === 'doctor' && callStatus === 'ready' && patient && (
                <button
                  onClick={() => callUser(patient.uid)}
                  disabled={!isPeerReady}
                  className="ml-2 px-2 py-0.5 bg-primary text-white text-[10px] font-bold rounded hover:bg-primary-dark disabled:opacity-50 transition-colors flex items-center"
                >
                  <Video className="w-3 h-3 mr-1" />
                  LIGAR AGORA
                </button>
              )}
              {user?.role === 'patient' && callStatus === 'ready' && doctor && (
                <button
                  onClick={() => callUser(doctor.uid)}
                  disabled={!isPeerReady}
                  className="ml-2 px-2 py-0.5 bg-primary text-white text-[10px] font-bold rounded hover:bg-primary-dark disabled:opacity-50 transition-colors flex items-center"
                >
                  <Video className="w-3 h-3 mr-1" />
                  CHAMAR MÉDICO
                </button>
              )}
              {doctor && (
                <button
                  onClick={() => setShowDoctorInfo(!showDoctorInfo)}
                  className="ml-2 text-text-muted hover:text-primary transition-colors"
                  title="Informações do Médico"
                >
                  <User className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Doctor Info Modal (Mobile/Tablet) */}
        {showDoctorInfo && doctor && (
          <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm lg:hidden">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-text-main border border-text-muted/20 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            >
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-primary mr-2" />
                  <h3 className="text-white font-bold font-display">Informações do Médico</h3>
                </div>
                <button onClick={() => setShowDoctorInfo(false)} className="text-text-muted hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Nome</p>
                  <p className="text-white font-bold">Dr(a). {doctor.name}</p>
                </div>
                {doctor.specialty && (
                  <div>
                    <p className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Especialidade</p>
                    <p className="text-text-muted">{doctor.specialty}</p>
                  </div>
                )}
                {doctor.crm && (
                  <div>
                    <p className="text-[10px] text-text-muted font-bold uppercase tracking-wider">CRM</p>
                    <p className="text-text-muted font-mono">{doctor.crm}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Status Indicator (Removed as it's now        {/* Doctor Info & Call History (Patient only) */}
        {user?.role === 'patient' && doctor && (
          <div className="absolute top-4 left-4 z-10 w-64 hidden lg:block space-y-4">
            <div className="bg-text-main/60 backdrop-blur-sm border border-text-muted/20 rounded-xl p-3 shadow-lg">
              <div className="flex items-center mb-2">
                <User className="w-4 h-4 text-primary mr-2" />
                <span className="text-xs font-bold text-white uppercase tracking-wider">Informações do Médico</span>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-bold text-white">Dr(a). {doctor.name}</p>
                {doctor.specialty && (
                  <p className="text-xs text-text-muted">{doctor.specialty}</p>
                )}
                {doctor.crm && (
                  <p className="text-[10px] text-text-muted font-mono">CRM: {doctor.crm}</p>
                )}
              </div>
            </div>

            {callLogs && callLogs.length > 0 && (
              <div className="bg-text-main/60 backdrop-blur-sm border border-text-muted/20 rounded-xl p-3 shadow-lg">
                <div className="flex items-center mb-2">
                  <Activity className="w-4 h-4 text-primary mr-2" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Histórico da Sessão</span>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                  {callLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center">
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          log.status === 'ended' ? 'bg-primary' :
                          log.status === 'failed' ? 'bg-secondary-dark' : 'bg-secondary'
                        }`} />
                        <span className="text-text-muted font-mono">{new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <span className="text-text-muted font-bold">{log.duration}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-text-muted/20 flex justify-between items-center">
                  <span className="text-[10px] text-text-muted uppercase font-bold">Total</span>
                  <span className="text-[10px] text-primary font-bold">
                    {formatDuration(callLogs.reduce((acc, log) => acc + (log.durationMs || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Patient Summary & Call History (Doctor only) */}
        {user?.role === 'doctor' && patient && (
          <div className="absolute top-24 right-4 z-10 w-64 hidden lg:block space-y-4">
            {(patient.allergies?.length || patient.preExistingConditions?.length || previousPrescriptions.length > 0) && (
              <div className="bg-text-main/60 backdrop-blur-sm border border-text-muted/20 rounded-xl p-3 shadow-lg">
                <div className="flex items-center mb-2">
                  <AlertCircle className="w-4 h-4 text-secondary-dark mr-2" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Resumo Crítico</span>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] text-text-muted font-bold uppercase">ID do Paciente</p>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-text-muted font-mono truncate mr-2">{patient.uid}</p>
                      {callStatus !== 'connected' && (
                        <button
                          onClick={() => callUser(patient.uid)}
                          disabled={!isPeerReady}
                          className="p-1 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50 transition-colors"
                          title="Iniciar Chamada"
                        >
                          <Video className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  {patient.allergies && patient.allergies.length > 0 && (
                    <div>
                      <p className="text-[10px] text-secondary-dark font-bold uppercase">Alergias</p>
                      <p className="text-xs text-text-muted truncate">{patient.allergies.join(', ')}</p>
                    </div>
                  )}
                  {patient.preExistingConditions && patient.preExistingConditions.length > 0 && (
                    <div>
                      <p className="text-[10px] text-secondary font-bold uppercase">Condições</p>
                      <p className="text-xs text-text-muted truncate">{patient.preExistingConditions.join(', ')}</p>
                    </div>
                  )}
                  {previousPrescriptions.length > 0 && (
                    <div>
                      <p className="text-[10px] text-primary font-bold uppercase">Última Prescrição</p>
                      <p className="text-xs text-text-muted">
                        {new Date(previousPrescriptions[0].createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {callLogs && callLogs.length > 0 && (
              <div className="bg-text-main/60 backdrop-blur-sm border border-text-muted/20 rounded-xl p-3 shadow-lg">
                <div className="flex items-center mb-2">
                  <Activity className="w-4 h-4 text-primary mr-2" />
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Histórico da Sessão</span>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                  {callLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center">
                        <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                          log.status === 'ended' ? 'bg-primary' :
                          log.status === 'failed' ? 'bg-secondary-dark' : 'bg-secondary'
                        }`} />
                        <span className="text-text-muted font-mono">{new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <span className="text-text-muted font-bold">{log.duration}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-text-muted/20 flex justify-between items-center">
                  <span className="text-[10px] text-text-muted uppercase font-bold">Total</span>
                  <span className="text-[10px] text-primary font-bold">
                    {formatDuration(callLogs.reduce((acc, log) => acc + (log.durationMs || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {remoteStream ? (
          <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-contain" />
        ) : (
          <div className="text-center text-text-muted">
            <Video className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-display">Aguardando conexão...</p>
            {user?.role === 'doctor' && patient && (
              <button
                onClick={() => callUser(patient.uid)}
                disabled={!isPeerReady}
                className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 font-bold transition-all"
              >
                Chamar Paciente
              </button>
            )}
            {user?.role === 'patient' && doctor && (
              <button
                onClick={() => callUser(doctor.uid)}
                disabled={!isPeerReady}
                className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 font-bold transition-all"
              >
                Chamar Médico
              </button>
            )}
          </div>
        )}

        {/* My Video Overlay */}
        <div className="absolute bottom-4 right-4 w-48 h-36 bg-text-main rounded-lg overflow-hidden border-2 border-text-muted/20 shadow-xl group">
          <video ref={myVideoRef} autoPlay playsInline muted className={`w-full h-full object-cover transition-opacity duration-300 ${!videoEnabled ? 'opacity-0' : 'opacity-100'}`} />
          {!videoEnabled && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-text-main">
              <VideoOff className="w-10 h-10 text-text-muted/40 mb-2" />
              <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Vídeo Desativado</span>
            </div>
          )}
          {!audioEnabled && (
            <div className="absolute top-2 right-2 bg-secondary-dark p-1 rounded-full shadow-lg">
              <MicOff className="w-3 h-3 text-white" />
            </div>
          )}
        </div>

        {/* Controls Overlay */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 bg-text-main/80 backdrop-blur-md p-4 rounded-2xl border border-text-muted/20 shadow-2xl">
          <button
            onClick={toggleAudio}
            className={`p-4 rounded-full transition-colors ${audioEnabled ? 'bg-text-muted/20 text-white hover:bg-text-muted/40' : 'bg-secondary-dark text-white hover:bg-secondary-dark/80'}`}
            title={audioEnabled ? "Desativar Microfone" : "Ativar Microfone"}
          >
            {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </button>
          <button
            onClick={toggleVideo}
            className={`p-4 rounded-full transition-colors ${videoEnabled ? 'bg-text-muted/20 text-white hover:bg-text-muted/40' : 'bg-secondary-dark text-white hover:bg-secondary-dark/80'}`}
            title={videoEnabled ? "Desativar Vídeo" : "Ativar Vídeo"}
          >
            {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>
          <button
            onClick={endCall}
            className="p-4 bg-secondary-dark text-white rounded-full hover:bg-secondary-dark/80 transition-colors"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
          <button
            onClick={shareToWhatsApp}
            className="p-4 bg-[#25D366] text-white rounded-full hover:bg-[#128C7E] transition-all shadow-lg hover:scale-110 active:scale-95"
            title="Compartilhar no WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </button>
          <button
            onClick={copyLink}
            className="p-4 bg-text-muted/20 text-white rounded-full hover:bg-text-muted/40 transition-all shadow-lg hover:scale-110 active:scale-95"
            title="Copiar Link da Consulta"
          >
            <Share2 className="w-6 h-6" />
          </button>
          <button
            onClick={() => {
              setShowCallLogs(!showCallLogs);
              setShowMedicalHistory(false);
              setIsPrescribing(false);
              setShowDoctorInfo(false);
            }}
            className={`p-4 rounded-full transition-colors ${showCallLogs ? 'bg-primary text-white' : 'bg-text-muted/20 text-white hover:bg-text-muted/40'}`}
            title="Logs da Chamada"
          >
            <Activity className="w-6 h-6" />
          </button>
          {user?.role === 'doctor' && (
            <>
              {callStatus !== 'connected' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => callUser(patient?.uid || '')}
                    disabled={!isPeerReady || !patient}
                    className="p-4 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors shadow-lg hover:scale-110 active:scale-95"
                    title="Iniciar Chamada com Paciente"
                  >
                    <Video className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => {
                      setShowManualCall(true);
                      setManualCallId(patient?.uid || '');
                    }}
                    disabled={!isPeerReady}
                    className="p-4 bg-text-muted/20 text-white rounded-full hover:bg-text-muted/40 transition-colors shadow-lg hover:scale-110 active:scale-95"
                    title="Ligar para ID Específico"
                  >
                    <RotateCcw className="w-6 h-6" />
                  </button>
                </div>
              )}
              <button
                onClick={() => {
                  setShowMedicalHistory(!showMedicalHistory);
                  setShowCallLogs(false);
                  setIsPrescribing(false);
                }}
                className={`p-4 rounded-full transition-colors ${showMedicalHistory ? 'bg-primary text-white' : 'bg-text-muted/20 text-white hover:bg-text-muted/40'}`}
                title="Histórico Médico"
              >
                <History className="w-6 h-6" />
              </button>
              <button
                onClick={() => {
                  setIsPrescribing(!isPrescribing);
                  setShowMedicalHistory(false);
                  setShowCallLogs(false);
                }}
                className={`p-4 rounded-full transition-colors ${isPrescribing ? 'bg-primary text-white' : 'bg-text-muted/20 text-white hover:bg-text-muted/40'}`}
                title="Prescrever"
              >
                <Clipboard className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Side Panel */}
      {showManualCall && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-surface border border-text-muted/20 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-text-main font-bold font-display">Iniciar Chamada por ID</h3>
              <button onClick={() => setShowManualCall(false)} className="text-text-muted hover:text-text-main transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">ID do Destinatário</label>
                <input
                  type="text"
                  value={manualCallId}
                  onChange={(e) => setManualCallId(e.target.value)}
                  placeholder="Cole o ID aqui..."
                  className="w-full mt-1 bg-bg-light border-text-muted/20 rounded-lg text-text-main text-sm focus:ring-primary focus:border-primary transition-all"
                />
              </div>
              <button
                onClick={() => {
                  callUser(manualCallId);
                  setShowManualCall(false);
                }}
                disabled={!manualCallId.trim() || !isPeerReady}
                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark disabled:opacity-50 transition-all shadow-lg shadow-primary/20"
              >
                Ligar Agora
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showCopiedToast && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50 bg-text-main text-white px-4 py-2 rounded-full shadow-2xl border border-text-muted/20 flex items-center space-x-2"
        >
          <CheckCircle2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold">Link copiado!</span>
        </motion.div>
      )}

      {showCallLogs && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          className="w-full lg:w-1/3 bg-surface border-l border-text-muted/10 flex flex-col h-full shadow-2xl z-20"
        >
          <div className="p-6 border-b border-text-muted/10 flex justify-between items-center bg-bg-light">
            <h2 className="text-xl font-bold text-text-main font-display flex items-center">
              <Activity className="w-5 h-5 mr-2 text-primary" /> Logs da Sessão
            </h2>
            <button onClick={() => setShowCallLogs(false)} className="text-text-muted hover:text-text-main transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-grow overflow-y-auto p-6 space-y-4">
            {callLogs && callLogs.length > 0 && (
              <div className="bg-primary/5 border border-primary/10 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">Tempo Total em Chamada</span>
                  <span className="text-lg font-bold text-text-main font-display">
                    {formatDuration(callLogs.reduce((acc, log) => acc + (log.durationMs || 0), 0))}
                  </span>
                </div>
              </div>
            )}

            {callLogs && callLogs.length > 0 ? (
              callLogs.map((log) => (
                <div key={log.id} className="bg-bg-light border border-text-muted/10 rounded-xl p-4 hover:border-primary/30 transition-all">
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                      log.status === 'ended' ? 'bg-primary/10 text-primary' :
                      log.status === 'failed' ? 'bg-secondary-dark/10 text-secondary-dark' :
                      log.status === 'missed' ? 'bg-secondary/10 text-secondary' :
                      'bg-text-muted/10 text-text-muted'
                    }`}>
                      {log.status === 'ended' ? 'Concluída' : 
                       log.status === 'failed' ? 'Falhou' : 
                       log.status === 'missed' ? 'Perdida' : 'Conectada'}
                    </span>
                    <span className="text-xs text-text-muted font-mono">
                      {new Date(log.startTime).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-text-muted">
                      <History className="w-4 h-4 mr-2 opacity-50" />
                      <span className="text-sm">Duração</span>
                    </div>
                    <span className="text-sm font-bold text-text-main">{log.duration}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-text-muted/20 mx-auto mb-4" />
                <p className="text-text-muted italic">Nenhuma chamada registrada nesta sessão.</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {showMedicalHistory && user?.role === 'doctor' && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          className="w-full lg:w-1/3 bg-surface border-l border-text-muted/10 flex flex-col h-full shadow-2xl z-20"
        >
          <div className="p-6 border-b border-text-muted/10 flex justify-between items-center bg-bg-light">
            <h2 className="text-xl font-bold text-text-main font-display flex items-center">
              <History className="w-5 h-5 mr-2 text-primary" /> Histórico Médico
            </h2>
            <div className="flex items-center space-x-2">
              {!isEditingHistory ? (
                <button
                  onClick={() => {
                    setTempAllergies(patient?.allergies || []);
                    setTempConditions(patient?.preExistingConditions || []);
                    setIsEditingHistory(true);
                  }}
                  className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                  title="Editar Histórico"
                >
                  <Edit className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={updatePatientHistory}
                  className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                  title="Salvar Histórico"
                >
                  <Save className="w-5 h-5" />
                </button>
              )}
              <button onClick={() => setShowMedicalHistory(false)} className="text-text-muted hover:text-text-main transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="flex-grow overflow-y-auto p-6 space-y-8">
            {/* Triage Notes */}
            {appointment?.triageNotes && (
              <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                <h3 className="text-sm font-bold text-primary uppercase tracking-wider flex items-center mb-2">
                  <Clipboard className="w-4 h-4 mr-1" /> Notas da Triagem
                </h3>
                <p className="text-text-main text-sm italic">"{appointment.triageNotes}"</p>
              </div>
            )}

            {/* Allergies & Conditions */}
            <div className="space-y-4">
              <div className="bg-secondary-dark/5 p-4 rounded-lg border border-secondary-dark/10">
                <h3 className="text-sm font-bold text-secondary-dark uppercase tracking-wider flex items-center mb-2">
                  <AlertCircle className="w-4 h-4 mr-1" /> Alergias
                </h3>
                {isEditingHistory ? (
                  <div className="space-y-2">
                    {tempAllergies.map((allergy, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={allergy}
                          onChange={(e) => {
                            const updated = [...tempAllergies];
                            updated[idx] = e.target.value;
                            setTempAllergies(updated);
                          }}
                          className="flex-grow text-sm p-1 border border-secondary-dark/20 rounded bg-white focus:ring-secondary-dark focus:border-secondary-dark"
                        />
                        <button
                          onClick={() => setTempAllergies(tempAllergies.filter((_, i) => i !== idx))}
                          className="text-secondary-dark/60 hover:text-secondary-dark transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setTempAllergies([...tempAllergies, ''])}
                      className="text-xs text-secondary-dark font-bold flex items-center hover:underline"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Adicionar Alergia
                    </button>
                  </div>
                ) : (
                  patient?.allergies && patient.allergies.length > 0 ? (
                    <ul className="list-disc list-inside text-text-main text-sm space-y-1">
                      {patient.allergies.map((allergy, idx) => (
                        <li key={idx}>{allergy}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-text-muted text-sm italic">Nenhuma alergia registrada.</p>
                  )
                )}
              </div>

              <div className="bg-secondary/5 p-4 rounded-lg border border-secondary/10">
                <h3 className="text-sm font-bold text-secondary uppercase tracking-wider flex items-center mb-2">
                  <Clipboard className="w-4 h-4 mr-1" /> Condições Pré-existentes
                </h3>
                {isEditingHistory ? (
                  <div className="space-y-2">
                    {tempConditions.map((condition, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={condition}
                          onChange={(e) => {
                            const updated = [...tempConditions];
                            updated[idx] = e.target.value;
                            setTempConditions(updated);
                          }}
                          className="flex-grow text-sm p-1 border border-secondary/20 rounded bg-white focus:ring-secondary focus:border-secondary"
                        />
                        <button
                          onClick={() => setTempConditions(tempConditions.filter((_, i) => i !== idx))}
                          className="text-secondary/60 hover:text-secondary transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setTempConditions([...tempConditions, ''])}
                      className="text-xs text-secondary font-bold flex items-center hover:underline"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Adicionar Condição
                    </button>
                  </div>
                ) : (
                  patient?.preExistingConditions && patient.preExistingConditions.length > 0 ? (
                    <ul className="list-disc list-inside text-text-main text-sm space-y-1">
                      {patient.preExistingConditions.map((condition, idx) => (
                        <li key={idx}>{condition}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-text-muted text-sm italic">Nenhuma condição registrada.</p>
                  )
                )}
              </div>
            </div>

            {/* Previous Prescriptions */}
            <div>
              <h3 className="text-lg font-bold text-text-main mb-4 flex items-center font-display">
                <Package className="w-5 h-5 mr-2 text-primary" /> Prescrições Anteriores
              </h3>
              <div className="space-y-4">
                {previousPrescriptions.length > 0 ? (
                  previousPrescriptions.map((pres) => (
                    <div key={pres.id} className="border border-text-muted/10 rounded-lg p-4 hover:bg-bg-light transition-all group">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-xs font-bold text-text-muted uppercase tracking-wider">
                            {new Date(pres.createdAt).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-[10px] text-text-muted/60 font-mono">ID: {pres.id.slice(0, 8)}</p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedMedications([...selectedMedications, ...pres.medications]);
                            setIsPrescribing(true);
                            setShowMedicalHistory(false);
                          }}
                          className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-all opacity-0 group-hover:opacity-100 flex items-center text-[10px] font-bold uppercase tracking-wider"
                          title="Repetir Prescrição"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> REPETIR
                        </button>
                      </div>
                      <div className="space-y-2">
                        {pres.medications.map((med, idx) => (
                          <div key={idx} className="text-sm">
                            <p className="font-bold text-text-main">{med.name}</p>
                            <p className="text-text-muted text-xs">{med.dosage} - {med.instructions}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-text-muted text-sm italic text-center py-8">Nenhuma prescrição anterior encontrada.</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {isPrescribing && user?.role === 'doctor' && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          className="w-full lg:w-1/3 bg-surface border-l border-text-muted/10 flex flex-col h-full shadow-2xl z-20"
        >
          <div className="p-6 border-b border-text-muted/10 flex justify-between items-center bg-bg-light">
            <h2 className="text-xl font-bold text-text-main font-display flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary" /> Prescrição Médica
            </h2>
            <button onClick={() => setIsPrescribing(false)} className="text-text-muted hover:text-text-main transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-grow overflow-y-auto p-6 space-y-8">
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
              <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Paciente</p>
              <p className="text-text-main font-bold">{patient?.name}</p>
              <p className="text-xs text-text-muted">CPF: {patient?.cpf}</p>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-text-main font-display">Medicamentos</h3>
                <button
                  onClick={() => addMedication()}
                  className="text-xs font-bold text-primary flex items-center hover:text-primary-dark transition-colors uppercase tracking-wider"
                >
                  <Plus className="w-4 h-4 mr-1" /> Manual
                </button>
              </div>

              {/* Medication Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar na base da Novità..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border-text-muted/20 rounded-lg text-sm focus:ring-primary focus:border-primary transition-all"
                />
                {searchTerm && (
                  <div className="absolute z-30 w-full mt-1 bg-surface border border-text-muted/10 rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar">
                    {medications
                      .filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(m => (
                        <button
                          key={m.id}
                          onClick={() => {
                            addMedication(m);
                            setSearchTerm('');
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-bg-light border-b border-text-muted/5 last:border-0 transition-colors"
                        >
                          <p className="font-bold text-text-main">{m.name}</p>
                          <p className="text-xs text-text-muted">{m.activeIngredient} - {m.presentations}</p>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Selected Medications */}
              <div className="space-y-4">
                {selectedMedications.map((med, index) => (
                  <div key={index} className="p-4 bg-bg-light rounded-lg border border-text-muted/10 relative group">
                    <button
                      onClick={() => removeMedication(index)}
                      className="absolute top-2 right-2 text-text-muted/40 hover:text-secondary-dark transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={med.name}
                        onChange={(e) => updateMedication(index, 'name', e.target.value)}
                        placeholder="Nome do medicamento"
                        className="w-full bg-transparent border-0 border-b border-text-muted/20 focus:ring-0 focus:border-primary font-bold p-0 text-text-main"
                      />
                      <input
                        type="text"
                        value={med.dosage}
                        onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                        placeholder="Posologia (ex: 1 comprimido)"
                        className="w-full bg-transparent border-0 border-b border-text-muted/20 focus:ring-0 focus:border-primary text-sm p-0 text-text-main"
                      />
                      <textarea
                        value={med.instructions}
                        onChange={(e) => updateMedication(index, 'instructions', e.target.value)}
                        placeholder="Instruções de uso..."
                        className="w-full bg-transparent border-0 border-b border-text-muted/20 focus:ring-0 focus:border-primary text-sm p-0 resize-none text-text-main"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Digital Certificate Alert */}
            {!digitalCertificate && (
              <div className="p-4 bg-secondary/5 rounded-lg border border-secondary/10 flex items-start">
                <AlertCircle className="w-5 h-5 text-secondary mr-3 mt-0.5" />
                <p className="text-xs text-secondary-dark">
                  Certificado digital não encontrado. A prescrição será emitida com assinatura manual.
                  Faça o upload do seu certificado no perfil.
                </p>
              </div>
            )}
          </div>

          <div className="p-6 bg-bg-light border-t border-text-muted/10">
            <button
              onClick={savePrescription}
              disabled={
                selectedMedications.length === 0 || 
                selectedMedications.some(med => !med.name.trim() || !med.dosage.trim())
              }
              className="w-full flex justify-center items-center py-3 px-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark disabled:opacity-50 shadow-lg shadow-primary/20 transition-all uppercase tracking-wider"
            >
              <Save className="w-5 h-5 mr-2" /> Finalizar e Assinar
            </button>
            {selectedMedications.length > 0 && selectedMedications.some(med => !med.name.trim() || !med.dosage.trim()) && (
              <p className="text-[10px] text-secondary-dark mt-2 text-center font-bold uppercase tracking-widest">
                Preencha o nome e a posologia de todos os medicamentos
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Patient Info Panel (Always visible for doctor, toggle for patient) */}
      {!isPrescribing && (
        <div className="hidden lg:flex w-1/4 bg-surface border-l border-text-muted/10 flex-col h-full">
          <div className="p-6 border-b border-text-muted/10 bg-bg-light">
            <h2 className="text-lg font-bold text-text-main font-display">Detalhes do Atendimento</h2>
          </div>
          <div className="p-6 space-y-6 overflow-y-auto">
            <div>
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Paciente</p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-text-main">{patient?.name}</p>
                  <p className="text-xs text-text-muted">CPF: {patient?.cpf}</p>
                </div>
              </div>
            </div>
            
            {appointment?.triageNotes && (
              <div>
                <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Notas de Triagem</p>
                <div className="p-3 bg-bg-light rounded-lg border border-text-muted/10 text-sm text-text-main italic">
                  "{appointment.triageNotes}"
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Especialidade</p>
              <p className="text-text-main font-medium">{appointment?.specialty || 'Clínico Geral'}</p>
            </div>

            <div className="pt-6 border-t border-text-muted/10">
              <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-4">Arquivos e Exames</p>
              <div className="space-y-2">
                {appointment?.examUrls?.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-2 rounded-lg hover:bg-bg-light border border-text-muted/10 text-xs font-medium text-text-main transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-2 text-primary" /> Exame {i + 1}
                  </a>
                ))}
                {(!appointment?.examUrls || appointment.examUrls.length === 0) && (
                  <p className="text-xs text-text-muted italic">Nenhum arquivo anexado.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
